import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Chip,
  OutlinedInput,
} from '@mui/material';
import { toast } from 'react-toastify';
import api from '../services/api';
import { getServiceTypes } from '../services/serviceType.service';
import { useAuth } from '../hooks/useAuth';
import { US_STATES } from '../data/usLocations';
import { INTERPRETER_LANGUAGES } from '../data/interpreterLanguages';

interface Firm {
  id: number;
  name: string;
}

interface MemberOption {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
  phone_extension?: string | null;
  designation?: { id: number; name: string } | null;
}

interface ServiceType {
  id: number;
  name: string;
  rush_tiers?: { key: string; label: string; min_days: number; max_days: number; fee: number }[];
}

function hasRole(user: any, roleName: string) {
  return user?.roles?.some((r: any) => r.name === roleName);
}

const CaseForm: React.FC<{ onSuccess?: () => void }> = ({ onSuccess }) => {
  const { user, loading } = useAuth();
  console.log('CaseForm user:', user);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [loadingServiceTypes, setLoadingServiceTypes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paralegals, setParalegals] = useState<MemberOption[]>([]);
  const [attorneys, setAttorneys] = useState<MemberOption[]>([]);
  const [loadingParalegals, setLoadingParalegals] = useState(false);
  const [opportunityTypes, setOpportunityTypes] = useState<{ id: number; name: string }[]>([]);
  const [salesStages, setSalesStages] = useState<{ id: number; name: string }[]>([]);
  const [form, setForm] = useState({
    firm_id: hasRole(user, 'firm_admin') ? user.firm_id : '',
    case_name: '',
    case_type: '',
    case_state: '',
    service_type_id: '',
    due_date: '',
    interpreter_languages: [] as string[],
    interpreter_other_language: '',
    paralegal_id: '',
    attorney_id: '',
    opportunity_id: '',
    file_number: '',
    claim_number: '',
    sales_stage_id: '',
    opportunity_follow_up: '',
  });
  const [languagesOpen, setLanguagesOpen] = useState(false);
  const [isFormValid, setIsFormValid] = useState(true);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (hasRole(user, 'admin')) {
      api.get('/admin/firms').then(res => {
        setFirms(Array.isArray(res.data.data) ? res.data.data : []);
      });
    }
    api.get('/admin/masters/opportunity-case-types', { params: { active_only: 1 } })
      .then(res => setOpportunityTypes(res.data.data ?? []))
      .catch(() => setOpportunityTypes([]));
    if (hasRole(user, 'admin')) {
      api.get('/admin/masters/sales-stages', { params: { active_only: 1 } })
        .then(res => setSalesStages(res.data.data ?? []))
        .catch(() => setSalesStages([]));
    }
    // Service types are loaded when case type is selected

    // Update form with firm_id for firm_admin users
    setForm(f => ({
      ...f,
      firm_id: hasRole(user, 'firm_admin') ? user.firm_id : f.firm_id,
    }));

    console.log('Form updated with user data:', {
      user_id: user?.id,
      user_firm_id: user?.firm_id,
      is_firm_admin: hasRole(user, 'firm_admin'),
      form_firm_id: hasRole(user, 'firm_admin') ? user.firm_id : '',
    });
  }, [user]);

  useEffect(() => {
    if (!form.case_type) {
      setServiceTypes([]);
      return;
    }

    setLoadingServiceTypes(true);
    getServiceTypes(form.case_type)
      .then(res => setServiceTypes(res.data))
      .catch(() => {
        toast.error('Failed to load service types');
        setServiceTypes([]);
      })
      .finally(() => setLoadingServiceTypes(false));
  }, [form.case_type]);

  // Load paralegals when the selected firm changes
  useEffect(() => {
    const firmId = hasRole(user, 'firm_admin') ? user?.firm_id : form.firm_id;
      if (!firmId) {
      setParalegals([]);
      setAttorneys([]);
      return;
    }
    setLoadingParalegals(true);
    api
      .get(`/admin/firms/${firmId}/members`)
      .then(res => {
        const members: MemberOption[] = res.data.data ?? [];
        setAttorneys(members.filter(member => member.designation?.name === 'Attorney'));
        setParalegals(members.filter(member => member.designation?.name === 'Paralegal'));
      })
      .catch(() => {
        setAttorneys([]);
        setParalegals([]);
      })
      .finally(() => setLoadingParalegals(false));
  }, [form.firm_id, user]);

  const handleInterpreterLanguagesChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const languages = typeof value === 'string' ? value.split(',') : value;
    setForm({
      ...form,
      interpreter_languages: languages,
      interpreter_other_language: languages.includes('other')
        ? form.interpreter_other_language
        : '',
    });
    setLanguagesOpen(false);
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const name = e.target.name;
    const value = (e.target as any).value;
    let updatedForm;

    if (name === 'case_type') {
      updatedForm = {
        ...form,
        case_type: value,
        service_type_id: '',
      };
    } else {
      updatedForm = { ...form, [name]: value };
    }

    setForm(updatedForm);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setSubmitting(true);

    // Ensure firm_id is set for firm_admin users
    const submitData = { ...form };
    console.log(hasRole(user, 'admin'));
    if (hasRole(user, 'firm_admin') && !hasRole(user, 'admin')) {
      submitData.firm_id = user.firm_id;
      submitData.opportunity_id = '';
      submitData.sales_stage_id = '';
      submitData.opportunity_follow_up = '';
    }

    if (
      submitData.interpreter_languages.includes('other') &&
      !submitData.interpreter_other_language.trim()
    ) {
      setErrors({ interpreter_other_language: ['Please specify the other language.'] });
      setSubmitting(false);
      return;
    }

    if (!submitData.interpreter_languages.includes('other')) {
      submitData.interpreter_other_language = '';
    }

    console.log('Submitting case data:', submitData);

    try {
      await api.post('/cases', submitData);
      onSuccess?.();
      
      // Reset form
      setForm({
        firm_id: hasRole(user, 'firm_admin') ? user.firm_id : '',
        case_name: '',
        case_type: '',
        case_state: '',
        service_type_id: '',
        due_date: '',
        interpreter_languages: [],
        interpreter_other_language: '',
        paralegal_id: '',
        attorney_id: '',
        opportunity_id: '',
        file_number: '',
        claim_number: '',
        sales_stage_id: '',
        opportunity_follow_up: '',
      });
      setLanguagesOpen(false);
      setServiceTypes([]);
      
      // Reset validation state
      setIsFormValid(true);
      setErrors({});
    } catch (err: any) {
      console.error('Case creation error:', err.response?.data);
      const errorData = err.response?.data?.errors || {};
      setErrors(errorData);

      // Show toast for general errors
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else if (Object.keys(errorData).length === 0) {
        toast.error('Failed to create case. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Box
      component='form'
      onSubmit={handleSubmit}
      sx={{ maxWidth: 500, mx: 'auto', p: 2 }}
    >
      {hasRole(user, 'admin') && (
        <FormControl
          fullWidth
          margin='normal'
          error={!!errors.firm_id}
          disabled={submitting}
        >
          <InputLabel id='firm-select-label'>Firm</InputLabel>
          <Select
            labelId='firm-select-label'
            name='firm_id'
            value={form.firm_id}
            onChange={handleChange}
            label='Firm'
            required
          >
            <MenuItem value=''>Select Firm</MenuItem>
            {Array.isArray(firms) &&
              firms.map(f => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name}
                </MenuItem>
              ))}
          </Select>
          {errors.firm_id && (
            <Typography color='error'>{errors.firm_id}</Typography>
          )}
        </FormControl>
      )}

      {hasRole(user, 'firm_admin') && user?.firm && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            <strong>Firm:</strong> {user.firm.name}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Case will be created for your firm automatically
          </Typography>
        </Box>
      )}

      {hasRole(user, 'admin') && (
        <TextField fullWidth margin='normal' label='Opportunity ID' name='opportunity_id' value={form.opportunity_id} onChange={handleChange} disabled={submitting} />
      )}
      <TextField fullWidth margin='normal' label='File Number' name='file_number' value={form.file_number} onChange={handleChange} disabled={submitting} />
      <TextField fullWidth margin='normal' label='Claim Number' name='claim_number' value={form.claim_number} onChange={handleChange} disabled={submitting} />

      {hasRole(user, 'admin') && (
      <FormControl fullWidth margin='normal' disabled={submitting}>
        <InputLabel id='sales-stage-label'>Sales Stage</InputLabel>
        <Select labelId='sales-stage-label' name='sales_stage_id' value={form.sales_stage_id} onChange={handleChange} label='Sales Stage'>
          <MenuItem value=''>Select</MenuItem>
          {salesStages.map(stage => (
            <MenuItem key={stage.id} value={stage.id}>{stage.name}</MenuItem>
          ))}
        </Select>
      </FormControl>
      )}

      {attorneys.length > 0 && (
        <FormControl fullWidth margin='normal' disabled={submitting}>
          <InputLabel id='attorney-label'>Attorney</InputLabel>
          <Select labelId='attorney-label' name='attorney_id' value={form.attorney_id} onChange={handleChange} label='Attorney'>
            <MenuItem value=''>— None —</MenuItem>
            {attorneys.map(member => (
              <MenuItem key={member.id} value={member.id}>
                {member.name} ({member.email})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Paralegal — optional, loads once a firm is selected */}
      {paralegals.length > 0 && (
        <FormControl fullWidth margin='normal' disabled={submitting}>
          <InputLabel id='paralegal-label'>Paralegal</InputLabel>
          <Select
            labelId='paralegal-label'
            name='paralegal_id'
            value={form.paralegal_id}
            onChange={handleChange}
            label='Paralegal'
          >
            <MenuItem value=''>— None —</MenuItem>
            {paralegals.map(p => (
              <MenuItem key={p.id} value={p.id}>
                {p.name} <span style={{ color: '#888', marginLeft: 6 }}>({p.email})</span>
              </MenuItem>
            ))}
          </Select>
          <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5 }}>
            If left empty, notifications will be sent to the firm admin as usual.
          </Typography>
        </FormControl>
      )}
      {!loadingParalegals && paralegals.length === 0 && (form.firm_id || hasRole(user, 'firm_admin')) && (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
          No paralegals linked to this firm.
        </Typography>
      )}

      {hasRole(user, 'admin') && (
      <TextField
        fullWidth
        margin='normal'
        label='Opportunity Follow Up'
        name='opportunity_follow_up'
        value={form.opportunity_follow_up}
        onChange={handleChange}
        disabled={submitting}
        multiline
        minRows={2}
      />
      )}

      <TextField
        fullWidth
        margin='normal'
        label='Client Name'
        name='case_name'
        value={form.case_name}
        onChange={handleChange}
        required
        error={!!errors.case_name}
        helperText={errors.case_name}
        disabled={submitting}
      />

      <FormControl
        fullWidth
        margin='normal'
        error={!!errors.case_state}
        disabled={submitting}
      >
        <InputLabel id='case-state-label'>State of Case</InputLabel>
        <Select
          labelId='case-state-label'
          name='case_state'
          value={form.case_state}
          onChange={handleChange}
          label='State of Case'
          required
        >
          <MenuItem value=''>Select State</MenuItem>
          {US_STATES.map(state => (
            <MenuItem key={state.abbreviation} value={state.abbreviation}>
              {state.name}
            </MenuItem>
          ))}
        </Select>
        {errors.case_state && (
          <Typography color='error'>{errors.case_state}</Typography>
        )}
      </FormControl>

      <FormControl
        fullWidth
        margin='normal'
        error={!!errors.case_type}
        disabled={submitting}
      >
        <InputLabel id='case-type-label'>Opportunity Case Type</InputLabel>
        <Select
          labelId='case-type-label'
          name='case_type'
          value={form.case_type}
          onChange={handleChange}
          label='Opportunity Case Type'
          required
        >
          <MenuItem value=''>Select</MenuItem>
          {opportunityTypes.map(type => (
            <MenuItem key={type.id} value={type.name}>{type.name}</MenuItem>
          ))}
        </Select>
        {errors.case_type && (
          <Typography color='error'>{errors.case_type}</Typography>
        )}
      </FormControl>

      <FormControl
        fullWidth
        margin='normal'
        error={!!errors.service_type_id}
        disabled={submitting || !form.case_type || loadingServiceTypes}
      >
        <InputLabel id='service-type-label'>Case Type</InputLabel>
        <Select
          labelId='service-type-label'
          name='service_type_id'
          value={form.service_type_id}
          onChange={handleChange}
          label='Case Type'
          required
        >
          <MenuItem value=''>
            {!form.case_type
              ? 'Select opportunity case type first'
              : loadingServiceTypes
                ? 'Loading case types...'
                : serviceTypes.length === 0
                  ? 'No case types available'
                  : 'Select'}
          </MenuItem>
          {serviceTypes.map((s: ServiceType) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
            </MenuItem>
          ))}
        </Select>
        {!form.case_type && (
          <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5 }}>
            Select an opportunity case type to see available case types
          </Typography>
        )}
        {errors.service_type_id && (
          <Typography color='error'>{errors.service_type_id}</Typography>
        )}
      </FormControl>

      <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
        <InputLabel id="interpreter-languages-label">Languages requiring interpreter</InputLabel>
        <Select
          labelId="interpreter-languages-label"
          id="interpreter_languages"
          multiple
          open={languagesOpen}
          onOpen={() => setLanguagesOpen(true)}
          onClose={() => setLanguagesOpen(false)}
          value={form.interpreter_languages}
          onChange={handleInterpreterLanguagesChange}
          input={<OutlinedInput label="Languages requiring interpreter" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip
                  key={value}
                  label={
                    value === 'other' && form.interpreter_other_language.trim()
                      ? `Other: ${form.interpreter_other_language.trim()}`
                      : INTERPRETER_LANGUAGES.find(l => l.code === value)?.label ?? value
                  }
                  size="small"
                />
              ))}
            </Box>
          )}
          disabled={submitting}
        >
          {INTERPRETER_LANGUAGES.map((language) => (
            <MenuItem key={language.code} value={language.code}>
              {language.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {form.interpreter_languages.includes('other') && (
        <TextField
          fullWidth
          margin="normal"
          label="Specify other language"
          name="interpreter_other_language"
          value={form.interpreter_other_language}
          onChange={handleChange}
          required
          disabled={submitting}
          error={!!errors.interpreter_other_language}
          helperText={
            errors.interpreter_other_language
              ? Array.isArray(errors.interpreter_other_language)
                ? errors.interpreter_other_language[0]
                : errors.interpreter_other_language
              : 'Enter the language name (e.g. Hindi, Tagalog).'
          }
        />
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        When selected, interpreter documents for configured languages are attached to the No Conflict email sent at case approval.
      </Typography>

      <Box mt={3}>
        <Button
          type='submit'
          variant='contained'
          color='primary'
          fullWidth
          disabled={submitting || !isFormValid}
          startIcon={
            submitting ? <CircularProgress size={20} color='inherit' /> : null
          }
        >
          {submitting ? 'Creating Case...' : 'Create Case'}
        </Button>
        {!isFormValid && (
          <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
            Please fix the errors before submitting
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default CaseForm;
